export type ManualOfferCallback = (sdp: RTCSessionDescriptionInit) => void;

/**
 * Defense-in-depth safety net around peer-lite's onnegotiationneeded handler.
 *
 * peer-lite's handler silently bails if `signalingState` is not 'stable'
 * by the time `createOffer()` resolves (a race against `signal(remoteOffer)`
 * being applied concurrently). In that case no SDP is ever emitted and we
 * rely on Chrome to re-fire negotiationneeded, which in the wild occasionally
 * fails to happen (~1 in 200) and produces the "blue screen" / no-video bug.
 *
 * This watchdog listens to signalingstatechange + negotiationneeded. After
 * the connection re-enters 'stable', if it finds any transceiver that is
 * not yet associated with an m-section (mid === null) — evidence that a
 * pending renegotiation was lost — it manually creates a fresh offer and
 * forwards it through the provided `onManualOffer` callback (routing it
 * back into peer-lite's normal signal pipeline).
 *
 * With the transceiver-first fix applied this should never actually fire;
 * its purpose is to prevent any future regression from re-introducing the
 * blue screen.
 */
export class NegotiationWatchdog {
  private static readonly DEBOUNCE_MS = 250;

  private pc: RTCPeerConnection | null = null;
  private onManualOffer: ManualOfferCallback | null = null;
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private inFlight = false;
  private detached = false;
  private activated = false;

  private readonly handleEvent = (): void => this.scheduleCheck();

  public attach(pc: RTCPeerConnection, onManualOffer: ManualOfferCallback): void {
    this.pc = pc;
    this.onManualOffer = onManualOffer;
    pc.addEventListener('signalingstatechange', this.handleEvent);
    pc.addEventListener('negotiationneeded', this.handleEvent);
  }

  public activate(): void {
    this.activated = true;
  }

  public detach(): void {
    this.detached = true;
    this.activated = false;
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
    if (this.pc) {
      this.pc.removeEventListener('signalingstatechange', this.handleEvent);
      this.pc.removeEventListener('negotiationneeded', this.handleEvent);
    }
    this.pc = null;
    this.onManualOffer = null;
  }

  /** Test hook: triggers the debounced check immediately, bypassing setTimeout. */
  public flushForTest(): Promise<void> {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
    return this.runCheck();
  }

  private scheduleCheck(): void {
    if (this.detached) return;
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => {
      this.debounceTimer = null;
      void this.runCheck();
    }, NegotiationWatchdog.DEBOUNCE_MS);
  }

  private async runCheck(): Promise<void> {
    if (this.detached || !this.pc || !this.onManualOffer) return;
    if (this.inFlight) return;
    if (!this.activated) return;
    if (this.pc.signalingState !== 'stable') return;
    if (!this.hasCompletedInitialNegotiation()) return;
    if (!this.hasUnnegotiatedTransceiver()) return;

    this.inFlight = true;
    try {
      const offer = await this.pc.createOffer();
      if (this.detached || !this.pc || !this.onManualOffer) return;
      if (this.pc.signalingState !== 'stable') return;
      await this.pc.setLocalDescription(offer);
      if (this.pc.localDescription) {
        console.warn(
          '[NegotiationWatchdog] Detected unnegotiated transceiver in stable state; firing manual re-offer.',
        );
        this.onManualOffer(this.pc.localDescription);
      }
    } catch (err) {
      console.warn('[NegotiationWatchdog] Manual re-offer failed:', err);
    } finally {
      this.inFlight = false;
    }
  }

  private hasUnnegotiatedTransceiver(): boolean {
    if (!this.pc) return false;
    return this.pc.getTransceivers().some((t) => {
      if (t.direction === 'inactive' || t.direction === 'stopped') return false;
      return t.mid === null || t.currentDirection === null;
    });
  }

  private hasCompletedInitialNegotiation(): boolean {
    if (!this.pc) return false;
    return this.pc.localDescription !== null && this.pc.remoteDescription !== null;
  }
}
